import React, { useState } from 'react';
import { Modal } from 'antd';
import COButton from '../COButton';
import styles from './CreateSupplierModal.module.scss';
import InputField from '../InputField';
import { Formik, Form } from 'formik';
import { Supplier } from 'models/Supplier/Supplier.model';
import { Fields, validationSchema } from './CreateSupplierModalConstants';
import { CreateSupplierModalProps } from 'enums/CreateSupplierModalEnums';
import Map from '../map';
import searchplaceByGeoCode from 'shared/utils/searchPlacByGeocode';
import clsx from 'clsx';
import fetchAddressFromGeoCode from 'shared/utils/fetchAddressFromGeoCode';
import { modalCloseIcon } from '../OrganizationModal';
import { SupplierCreationUpdation } from 'services/ValueChainService/supplier.service';

interface CreateSupplierFormValues {
  supplierName: string;
  streetLocality: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  latitude?: string;
  longitude?: string;
  country_code?: string;
}

interface CreateSupplierModal {
  showModalState: boolean;
  handleModalCancel: () => void;
  handleModalConfirm: (payload: boolean) => void;
  preFetchedData?: Supplier;
  createSupplier?: (supplier: SupplierCreationUpdation) => Promise<Supplier>;
  updateSupplier?: (
    id: number | undefined,
    supplier: SupplierCreationUpdation
  ) => Promise<void>;
  id: number;
  successCallback?: (supplierId: string) => void;
  loading?: boolean;
}

const CreateSupplierModal: React.FC<CreateSupplierModal> = ({
  showModalState,
  handleModalCancel,
  handleModalConfirm,
  preFetchedData,
  createSupplier,
  updateSupplier,
  id,
  successCallback,
  loading,
}: CreateSupplierModal) => {
  const preFetchedValue: CreateSupplierFormValues = {
    supplierName: preFetchedData?.name ?? '',
    streetLocality: preFetchedData?.address?.street ?? '',
    city: preFetchedData?.address?.city ?? '',
    province: preFetchedData?.address?.province ?? '',
    country: preFetchedData?.address?.country ?? '',
    postalCode: preFetchedData?.address?.postalCode ?? '',
    country_code: preFetchedData?.address?.countryCode ?? '',
    latitude: preFetchedData?.address?.latitude,
    longitude: preFetchedData?.address?.longitude,
  };

  const handleFormSubmission = async (values: CreateSupplierFormValues) => {
    try {
      let data;
      if (preFetchedData) {
        if (updateSupplier) {
          await updateSupplier(preFetchedData.id, {
            name: values.supplierName,
            company_id: id,
            address: {
              address_name: values.streetLocality,
              street: values.streetLocality,
              city: values.city,
              province: values.province,
              country: values.country,
              postal_code: values.postalCode,
              latitude: values.latitude,
              longitude: values.longitude,
              country_code: values.country_code,
            },
          });
        }
      } else {
        if (createSupplier) {
          data = await createSupplier({
            name: values.supplierName,
            company_id: id,
            address: {
              address_name: values.streetLocality,
              street: values.streetLocality,
              city: values.city,
              province: values.province,
              country: values.country,
              postal_code: values.postalCode,
              latitude: values.latitude,
              longitude: values.longitude,
              country_code: values.country_code,
            },
          });
        }
        successCallback && successCallback(data?.id?.toString() ?? '');
      }
      handleModalConfirm(false);
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  };

  const [fetchingAddress, setFetchingAddress] = useState<boolean>(false);

  const searchPlaceByCountryProvince = async (
    country: string,
    province: string,
    setFieldValue: (field: string, value: string | number) => void
  ) => {
    if (country === '' || province === '') return;
    try {
      setFetchingAddress(true);
      const results: google.maps.GeocoderResult[] | undefined =
        await searchplaceByGeoCode(`${province} ${country}`);
      if (results && results.length > 0 && results[0]) {
        const latling = results[0].geometry.location;
        const addressComponents = results[0].address_components;
        const formattedAddress = results[0].formatted_address;
        setFieldValue('map', formattedAddress);
        setAddressInState(addressComponents, latling, setFieldValue);
      }
    } catch (error) {
      console.error('Error in searchPlaceByCountryProvince:', error);
    } finally {
      setFetchingAddress(false);
    }
  };

  const setAddressInState = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latling: google.maps.LatLng | null,
    setFieldValue: (field: string, value: string | number) => void
  ) => {
    const { streetLocality, country, province, city, pinCode } =
      fetchAddressFromGeoCode(addressComponents);

    if (streetLocality.trim() === '') {
      setFieldValue('streetLocality', '');
    } else {
      setFieldValue('streetLocality', streetLocality?.trim() ?? '');
    }
    setFieldValue('country', country?.long_name ?? '');
    setFieldValue('country_code', country?.short_name ?? '');
    setFieldValue('province', province?.long_name ?? '');
    setFieldValue('city', city?.long_name ?? '');
    setFieldValue('postalCode', pinCode?.long_name ?? '');
    setFieldValue('latitude', latling?.lat() ?? '');
    setFieldValue('longitude', latling?.lng() ?? '');
  };

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      width={725}
      footer={null}
      wrapClassName={styles.modalContainer}
      open={showModalState}
      closeIcon={modalCloseIcon}
      onCancel={handleModalCancel}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <h2>
            {preFetchedData
              ? CreateSupplierModalProps.MODAL_HEADING_EDIT
              : CreateSupplierModalProps.MODAL_HEADING_CREATE}
          </h2>
        </div>
        <Formik
          enableReinitialize
          initialValues={preFetchedValue}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmission}>
          {({ setFieldValue, isSubmitting, values }) => (
            <Form>
              <div className={styles.fieldContainer}>
                {Fields.map(field => {
                  return field.google ? (
                    <div
                      className={clsx({
                        [styles.inputContainer]: true,
                        [styles.longContainer]: true,
                      })}
                      key={field.id}>
                      <div className={styles.mapContainer}>
                        <Map
                          name="map"
                          setAddress={(
                            addressComponents: google.maps.GeocoderAddressComponent[],
                            latling: google.maps.LatLng | null
                          ) =>
                            setAddressInState(
                              addressComponents,
                              latling,
                              setFieldValue
                            )
                          }
                          latitude={values.latitude}
                          longitude={values.longitude}
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      className={clsx({
                        [styles.inputContainer]: true,
                        [styles.longContainer]: field.long,
                      })}
                      key={field.id}>
                      <InputField
                        className={field.className}
                        name={field.name}
                        placeholderstring={field.placeholder}
                        label={field.label}
                        disabled={field.disabled}
                        type={
                          'type' in field
                            ? (field as { type?: React.HTMLInputTypeAttribute })
                                .type
                            : undefined
                        }
                        onBlur={() => {
                          if (
                            field.name === 'country' ||
                            field.name === 'province'
                          ) {
                            searchPlaceByCountryProvince(
                              values.country ?? '',
                              values.province ?? '',
                              setFieldValue
                            );
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div className={styles.buttonContainer}>
                <div className={styles.singleButtonContainer}>
                  <COButton submit loading={fetchingAddress || loading}>
                    {preFetchedData
                      ? CreateSupplierModalProps.EDIT_BUTTON_LABEL
                      : CreateSupplierModalProps.CREATE_BUTTON_LABEL}
                  </COButton>
                  <COButton
                    secondary
                    onClick={handleModalCancel}
                    disabled={loading}>
                    {CreateSupplierModalProps.CANCEL_BUTTON_LABEL}
                  </COButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default CreateSupplierModal;
